/**
 * Declarations (combinations) for Armenian Belot: detection, team resolution, Bella hook.
 *
 * Consecutive ranks in a suit follow the usual Belot line: 7–8–9–J–Q–K–10–A (`RANK_*` indices).
 *
 * **UI (после ревила):** итог сравнения для полоски мест — `client/src/game/belotArmenian/resolveDeclarationBattleOutcome.js`.
 *
 * **Casual:** `END_DECLARE_PHASE` runs full `detectCombinations` / `compareTeams` on the deal.
 * **Hardcore (локальный движок):** игрок передаёт объявленные ключи на первом ходу
 * (`COMMIT_FIRST_PLAY_DECLARATIONS` или `PLAY_CARD.payload.commitDeclaredKeys`); зачёт — только они.
 *
 * @module game/belot/armenian/declarations
 */

const {
  makeCard,
  cardSuit,
  cardRank,
  isValidCard,
  SUIT_MIN,
  SUIT_MAX,
  RANK_9,
  RANK_J,
  RANK_Q,
  RANK_K,
  RANK_10,
  RANK_A,
} = require("./types");
const { TRUMP_ALL_TRUMP } = require("./auction");
const { AUCTION_SUIT_STRENGTH } = require("./constants");

/** @typedef {'SEQUENCE'|'CARRE'} BelotComboKind */

/**
 * @typedef {{
 *   kind: BelotComboKind,
 *   label: 'TERZ'|'FIFTY'|'HUNDRED'|'CARRE_J'|'CARRE_9'|'CARRE_A'|'CARRE_10'|'CARRE_K'|'CARRE_Q',
 *   points: number,
 *   cards: number[],
 *   suit?: number,
 *   length?: number,
 *   highRank?: number,
 *   rank?: number,
 * }} BelotDeclaration
 */

const POINTS_TERZ = 20;
const POINTS_FIFTY = 50;
const POINTS_HUNDRED = 100;
const POINTS_CARRE_J = 200;
const POINTS_CARRE_9 = 150;
const POINTS_CARRE_FACE = 100;

const BELLA_POINTS = 20;

/**
 * @param {number[]} hand
 */
function assertHand(hand) {
  if (!Array.isArray(hand) || hand.length !== 8) {
    throw new Error("declarations: hand must be an array of 8 cards");
  }
  const seen = new Set();
  for (const c of hand) {
    if (!isValidCard(c)) throw new Error("declarations: invalid card");
    if (seen.has(c)) throw new Error("declarations: duplicate card");
    seen.add(c);
  }
}

/**
 * @param {number[]} sortedRanks unique sorted rank indices
 * @returns {number[][]} maximal consecutive runs, each length >= 3
 */
function consecutiveRunsFromSortedRanks(sortedRanks) {
  if (sortedRanks.length === 0) return [];
  /** @type {number[][]} */
  const runs = [];
  let start = 0;
  for (let i = 1; i <= sortedRanks.length; i += 1) {
    const end = i === sortedRanks.length || sortedRanks[i] !== sortedRanks[i - 1] + 1;
    if (end) {
      const slice = sortedRanks.slice(start, i);
      if (slice.length >= 3) runs.push(slice);
      start = i;
    }
  }
  return runs;
}

/**
 * @param {number[]} runRanks
 * @returns {number}
 */
function sequencePointsForLength(runRanks) {
  const n = runRanks.length;
  if (n >= 5) return POINTS_HUNDRED;
  if (n === 4) return POINTS_FIFTY;
  return POINTS_TERZ;
}

/**
 * @param {number[]} runRanks
 * @returns {'TERZ'|'FIFTY'|'HUNDRED'}
 */
function sequenceLabelForLength(runRanks) {
  const n = runRanks.length;
  if (n >= 5) return "HUNDRED";
  if (n === 4) return "FIFTY";
  return "TERZ";
}

/**
 * @param {number} rank
 * @returns {BelotDeclaration|null}
 */
function carreDeclarationFromRank(rank) {
  const cards = [];
  for (let s = SUIT_MIN; s <= SUIT_MAX; s += 1) {
    cards.push(makeCard(s, rank));
  }
  if (rank === RANK_J) {
    return {
      kind: "CARRE",
      label: "CARRE_J",
      points: POINTS_CARRE_J,
      cards,
      rank,
    };
  }
  if (rank === RANK_9) {
    return {
      kind: "CARRE",
      label: "CARRE_9",
      points: POINTS_CARRE_9,
      cards,
      rank,
    };
  }
  if (rank === RANK_A || rank === RANK_10 || rank === RANK_K || rank === RANK_Q) {
    const label =
      rank === RANK_A
        ? "CARRE_A"
        : rank === RANK_10
          ? "CARRE_10"
          : rank === RANK_K
            ? "CARRE_K"
            : "CARRE_Q";
    return {
      kind: "CARRE",
      label,
      points: POINTS_CARRE_FACE,
      cards,
      rank,
    };
  }
  return null;
}

/**
 * Стабильный ключ экземпляра комбинации на раздаче (для хардкора «объявить / скрыть»).
 *
 * @param {BelotDeclaration} d
 * @returns {string}
 */
function declarationComboKey(d) {
  if (!d || typeof d !== "object") return "";
  const label = typeof d.label === "string" ? d.label : "";
  const cards = Array.isArray(d.cards) ? [...d.cards].sort((a, b) => a - b) : [];
  return `${label}:${cards.join(",")}`;
}

/**
 * Уникальные карты `3..16` шт. (Open Belot: рука + стол) или 8 карт классической раздачи.
 *
 * @param {number[]} cards
 * @returns {BelotDeclaration[]}
 */
function detectCombinationsFromPool(cards) {
  if (!Array.isArray(cards) || cards.length < 3 || cards.length > 16) return [];
  const seen = new Set();
  for (const c of cards) {
    if (!isValidCard(c)) throw new Error("declarations.detectCombinationsFromPool: invalid card");
    if (seen.has(c)) throw new Error("declarations.detectCombinationsFromPool: duplicate card");
    seen.add(c);
  }
  /** @type {BelotDeclaration[]} */
  const out = [];
  /** @type {Map<number, number>} */
  const byRank = new Map();
  for (const c of cards) {
    const r = cardRank(c);
    byRank.set(r, (byRank.get(r) || 0) + 1);
  }
  for (const [rank, cnt] of byRank) {
    if (cnt === 4) {
      const d = carreDeclarationFromRank(rank);
      if (d) out.push(d);
    }
  }

  for (let suit = SUIT_MIN; suit <= SUIT_MAX; suit += 1) {
    const ranks = [];
    for (const c of cards) {
      if (cardSuit(c) === suit) ranks.push(cardRank(c));
    }
    ranks.sort((a, b) => a - b);
    const uniq = [...new Set(ranks)];
    const runs = consecutiveRunsFromSortedRanks(uniq);
    for (const run of runs) {
      const highRank = run[run.length - 1];
      const runCards = run.map((r) => makeCard(suit, r));
      out.push({
        kind: "SEQUENCE",
        label: sequenceLabelForLength(run),
        points: sequencePointsForLength(run),
        cards: runCards,
        suit,
        length: run.length,
        highRank,
      });
    }
  }
  return out;
}

/**
 * @param {number[]} hand 8 distinct valid cards
 * @returns {BelotDeclaration[]}
 */
function detectCombinations(hand) {
  assertHand(hand);
  return detectCombinationsFromPool(hand);
}

/**
 * @param {BelotDeclaration[]} combos
 * @returns {number}
 */
function sumComboPoints(combos) {
  if (!Array.isArray(combos)) return 0;
  let s = 0;
  for (const c of combos) s += Number(c?.points) || 0;
  return s;
}

/**
 * Sequence counts as «козырная» for tie-break when its suit is the contract trump (or any suit in all-trump).
 *
 * @param {BelotDeclaration} d
 * @param {number|null|typeof TRUMP_ALL_TRUMP|undefined} contractTrumpSuit
 * @returns {0|1}
 */
function sequenceTrumpTier(d, contractTrumpSuit) {
  if (d.kind !== "SEQUENCE" || d.suit == null) return 0;
  if (contractTrumpSuit === TRUMP_ALL_TRUMP) return 1;
  if (typeof contractTrumpSuit === "number" && d.suit === contractTrumpSuit) return 1;
  return 0;
}

/**
 * Compare two declarations for priority (higher = stronger).
 * Order: points, sequence length (carre = 4), high rank, then trump sequence vs non-trump, then all-trump suit order.
 *
 * @param {BelotDeclaration} a
 * @param {BelotDeclaration} b
 * @param {number|null|typeof TRUMP_ALL_TRUMP|undefined} [contractTrumpSuit] — omit for legacy compare (no trump tie-break).
 * @returns {number} >0 if a stronger, <0 if b stronger, 0 tie
 */
function compareDeclarations(a, b, contractTrumpSuit) {
  if (a.points !== b.points) return a.points - b.points;
  const lenA = a.kind === "SEQUENCE" && a.length != null ? a.length : 4;
  const lenB = b.kind === "SEQUENCE" && b.length != null ? b.length : 4;
  if (lenA !== lenB) return lenA - lenB;
  const hiA =
    a.kind === "SEQUENCE" && a.highRank != null ? a.highRank : a.rank ?? -1;
  const hiB =
    b.kind === "SEQUENCE" && b.highRank != null ? b.highRank : b.rank ?? -1;
  if (hiA !== hiB) return hiA - hiB;

  if (contractTrumpSuit !== undefined) {
    const tA = sequenceTrumpTier(a, contractTrumpSuit);
    const tB = sequenceTrumpTier(b, contractTrumpSuit);
    if (tA !== tB) return tA - tB;
    if (
      contractTrumpSuit === TRUMP_ALL_TRUMP &&
      a.kind === "SEQUENCE" &&
      b.kind === "SEQUENCE" &&
      a.suit != null &&
      b.suit != null
    ) {
      return AUCTION_SUIT_STRENGTH[a.suit] - AUCTION_SUIT_STRENGTH[b.suit];
    }
  }
  return 0;
}

/**
 * @param {BelotDeclaration[]} combos
 * @param {number|null|typeof TRUMP_ALL_TRUMP|undefined} [contractTrumpSuit]
 * @returns {BelotDeclaration|null}
 */
function strongestDeclaration(combos, contractTrumpSuit) {
  if (!combos.length) return null;
  let best = combos[0];
  for (let i = 1; i < combos.length; i += 1) {
    if (compareDeclarations(combos[i], best, contractTrumpSuit) > 0) best = combos[i];
  }
  return best;
}

/**
 * Winning team keeps all its declarations; losing team's declarations burn (0 points).
 * With `contractTrumpSuit`: trump sequence beats non-trump; NT or both off-trump same strength → all combo points burn.
 * If `contractTrumpSuit` is omitted, exact tie → both teams keep all (legacy).
 *
 * @param {BelotDeclaration[]} teamACombos
 * @param {BelotDeclaration[]} teamBCombos
 * @param {number|null|typeof TRUMP_ALL_TRUMP|undefined} [contractTrumpSuit]
 * @returns {{
 *   outcome: 'team_a'|'team_b'|'tie',
 *   scoringA: BelotDeclaration[],
 *   scoringB: BelotDeclaration[],
 *   burnedA: BelotDeclaration[],
 *   burnedB: BelotDeclaration[],
 * }}
 */
function compareTeams(teamACombos, teamBCombos, contractTrumpSuit) {
  const sa = strongestDeclaration(teamACombos, contractTrumpSuit);
  const sb = strongestDeclaration(teamBCombos, contractTrumpSuit);
  if (sa == null && sb == null) {
    return {
      outcome: "tie",
      scoringA: [],
      scoringB: [],
      burnedA: [],
      burnedB: [],
    };
  }
  if (sa == null) {
    return {
      outcome: "team_b",
      scoringA: [],
      scoringB: teamBCombos.slice(),
      burnedA: teamACombos.slice(),
      burnedB: [],
    };
  }
  if (sb == null) {
    return {
      outcome: "team_a",
      scoringA: teamACombos.slice(),
      scoringB: [],
      burnedA: [],
      burnedB: teamBCombos.slice(),
    };
  }
  const c = compareDeclarations(sa, sb, contractTrumpSuit);
  if (c === 0) {
    if (contractTrumpSuit !== undefined) {
      const bothSeq = sa.kind === "SEQUENCE" && sb.kind === "SEQUENCE";
      const ntBurn = contractTrumpSuit === null && bothSeq;
      const offTrumpBurn =
        typeof contractTrumpSuit === "number" &&
        bothSeq &&
        sa.suit != null &&
        sb.suit != null &&
        sa.suit !== contractTrumpSuit &&
        sb.suit !== contractTrumpSuit;
      if (ntBurn || offTrumpBurn) {
        return {
          outcome: "tie",
          scoringA: [],
          scoringB: [],
          burnedA: teamACombos.slice(),
          burnedB: teamBCombos.slice(),
        };
      }
    }
    return {
      outcome: "tie",
      scoringA: teamACombos.slice(),
      scoringB: teamBCombos.slice(),
      burnedA: [],
      burnedB: [],
    };
  }
  if (c > 0) {
    return {
      outcome: "team_a",
      scoringA: teamACombos.slice(),
      scoringB: [],
      burnedA: [],
      burnedB: teamBCombos.slice(),
    };
  }
  return {
    outcome: "team_b",
    scoringA: [],
    scoringB: teamBCombos.slice(),
    burnedA: teamACombos.slice(),
    burnedB: [],
  };
}

/**
 * Bella: trump K+Q in hand; announced when playing the king or queen of trump.
 *
 * @param {number} cardPlayed
 * @param {number[]} handBeforePlay 8 cards including `cardPlayed`
 * @param {number|null} trumpSuit
 * @returns {boolean}
 */
function canDeclareBellaOnPlay(cardPlayed, handBeforePlay, trumpSuit) {
  if (trumpSuit == null) return false;
  if (!isValidCard(cardPlayed) || !Array.isArray(handBeforePlay)) return false;
  if (cardSuit(cardPlayed) !== trumpSuit) return false;
  const r = cardRank(cardPlayed);
  if (r !== RANK_K && r !== RANK_Q) return false;
  const k = makeCard(trumpSuit, RANK_K);
  const q = makeCard(trumpSuit, RANK_Q);
  return handBeforePlay.includes(k) && handBeforePlay.includes(q);
}

module.exports = {
  POINTS_TERZ,
  POINTS_FIFTY,
  POINTS_HUNDRED,
  POINTS_CARRE_J,
  POINTS_CARRE_9,
  POINTS_CARRE_FACE,
  BELLA_POINTS,
  declarationComboKey,
  detectCombinations,
  detectCombinationsFromPool,
  sumComboPoints,
  compareDeclarations,
  strongestDeclaration,
  compareTeams,
  canDeclareBellaOnPlay,
};
